import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { apiGet, apiPost, apiPatch, apiDelete } from './client.js';
import type { Supplier, SupplierProduct } from './types.js';

const SUPPLIERS_KEY = ['suppliers'];

export const useSuppliers = (params?: Record<string, string | number | boolean>) => {
  const query = params ? '?' + new URLSearchParams(
    Object.entries(params).map(([k, v]) => [k, String(v)])
  ).toString() : '';
  return useQuery({
    queryKey: [...SUPPLIERS_KEY, params],
    queryFn: async () => {
      const res = await apiGet<{ data: Supplier[] }>(`/suppliers${query}`);
      return res?.data ?? [];
    },
  });
};

export const useSupplier = (id: string) => {
  return useQuery({
    queryKey: [...SUPPLIERS_KEY, id],
    queryFn: () => apiGet<Supplier>(`/suppliers/${id}`),
    enabled: !!id,
  });
};

export const useSupplierProducts = (supplierId: string) => {
  return useQuery({
    queryKey: [...SUPPLIERS_KEY, supplierId, 'products'],
    queryFn: () => apiGet<SupplierProduct[]>(`/suppliers/${supplierId}/products`),
    enabled: !!supplierId,
  });
};

export const useCreateSupplier = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<Supplier>) => apiPost<Supplier>('/suppliers', data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: SUPPLIERS_KEY }); },
    onError: (e: Error) => toast.error(e.message),
  });
};

export const useUpdateSupplier = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Supplier> }) =>
      apiPatch<Supplier>(`/suppliers/${id}`, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: SUPPLIERS_KEY }); },
    onError: (e: Error) => toast.error(e.message),
  });
};

export const useDeleteSupplier = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/suppliers/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: SUPPLIERS_KEY }); },
    onError: (e: Error) => toast.error(e.message),
  });
};

export const useBulkActivateSuppliers = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => apiPost('/suppliers/bulk/activate', { ids }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: SUPPLIERS_KEY }); },
    onError: (e: Error) => toast.error(e.message),
  });
};

export const useBulkDeactivateSuppliers = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => apiPost('/suppliers/bulk/deactivate', { ids }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: SUPPLIERS_KEY }); },
    onError: (e: Error) => toast.error(e.message),
  });
};

export const useBulkDeleteSuppliers = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => apiPost('/suppliers/bulk/delete', { ids }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: SUPPLIERS_KEY }); },
    onError: (e: Error) => toast.error(e.message),
  });
};

export const useCreateSupplierProduct = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ supplierId, data }: { supplierId: string; data: Partial<SupplierProduct> }) =>
      apiPost<SupplierProduct>(`/suppliers/${supplierId}/products`, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: SUPPLIERS_KEY }); },
    onError: (e: Error) => toast.error(e.message),
  });
};

export const useUpdateSupplierProduct = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ supplierId, productId, data }: { supplierId: string; productId: string; data: Partial<SupplierProduct> }) =>
      apiPatch<SupplierProduct>(`/suppliers/${supplierId}/products/${productId}`, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: SUPPLIERS_KEY }); },
    onError: (e: Error) => toast.error(e.message),
  });
};

export const useToggleSupplierProduct = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ supplierId, productId }: { supplierId: string; productId: string }) =>
      apiPatch<SupplierProduct>(`/suppliers/${supplierId}/products/${productId}/toggle`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: SUPPLIERS_KEY }); },
    onError: (e: Error) => toast.error(e.message),
  });
};
